import { useCallback, useRef } from 'react';
import { useNodeStore } from '@/stores/nodeStore';
import type { NodeState } from '@/stores/nodeStore';

export interface InputAsset {
  id: string;
  type: 'image' | 'video' | 'audio';
  src: string;
}

// P4: 自定义比较函数，比较 assets 数组内容而非引用
// 之前 useShallow 订阅整个 nodes 数组，拖拽停止时 updateNodePositions 改变被拖拽节点引用
// 导致 useShallow 判定数组变化，触发所有 GenerateNode 重渲染
// 现在只比较 inputIds 对应节点的 image/videoUri/audioUri，位置变化不触发重渲染
function assetsEqual(prev: InputAsset[], next: InputAsset[]): boolean {
  if (prev.length !== next.length) return false;
  return prev.every((p, i) => p.id === next[i].id && p.src === next[i].src && p.type === next[i].type);
}

// 类似 useShallow 的模式：用 ref 缓存上一次结果，自定义比较函数决定是否更新
function useCustomEqual<U>(selector: (state: NodeState) => U, equal: (prev: U, next: U) => boolean): (state: NodeState) => U {
  const prev = useRef<U | undefined>(undefined);
  return useCallback((state: NodeState) => {
    const next = selector(state);
    if (prev.current !== undefined && equal(prev.current, next)) {
      return prev.current;
    }
    prev.current = next;
    return next;
  }, [selector, equal]);
}

export function useInputAssets(inputIds: string[]) {
  const selector = useCallback((state: NodeState) => {
    const assets: InputAsset[] = [];
    const seen = new Set<string>();
    for (const inputId of inputIds) {
      if (seen.has(inputId)) continue;
      seen.add(inputId);
      const n = state.nodeMap.get(inputId);
      if (!n) continue;
      if (n.data.image) assets.push({ id: n.id, type: 'image', src: n.data.image });
      else if (n.data.videoUri) assets.push({ id: n.id, type: 'video', src: n.data.videoUri });
      else if (n.data.audioUri) assets.push({ id: n.id, type: 'audio', src: n.data.audioUri });
    }
    return assets;
  }, [inputIds]);

  const stableSelector = useCustomEqual(selector, assetsEqual);
  const inputAssets = useNodeStore(stableSelector);

  return inputAssets;
}
